import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, ShieldAlert, Sparkles, Power, RotateCw, Search, Trash2, Settings2, Lock, Send, Building2 } from "lucide-react";
import { toast } from "sonner";
import SendEmailDialog from "@/components/app/SendEmailDialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const hardCountClass = (n) => {
  if (n >= 3) return "text-red-700";
  if (n > 0) return "text-red-600 hover:underline cursor-pointer";
  return "text-zinc-400 cursor-default";
};
const softCountClass = (n) =>
  n > 0 ? "text-orange-600 hover:underline cursor-pointer" : "text-zinc-400 cursor-default";

const issueButtonClass = (mode, severity) => {
  if (mode === "appreciation") return "bg-emerald-600 hover:bg-emerald-700";
  if (severity === "hard") return "bg-red-600 hover:bg-red-700";
  return "bg-orange-600 hover:bg-orange-700";
};

export default function WarningsAdmin() {
  const { selectedId } = useProperty();
  const { hasPermission } = useAuth();
  const canRemove = hasPermission("warnings_remove");
  const canEmail = hasPermission("email_send");
  const [staff, setStaff] = useState([]);
  const [query, setQuery] = useState("");
  const [issueFor, setIssueFor] = useState(null);
  const [mode, setMode] = useState("warning"); // warning | appreciation
  const [severity, setSeverity] = useState("soft");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // Manage-warnings dialog
  const [manageFor, setManageFor] = useState(null);
  const [manageList, setManageList] = useState([]);
  const [manageBusyId, setManageBusyId] = useState(null);
  // Manual email
  const [emailFor, setEmailFor] = useState(null); // { user, category, placeholders }
  // Department-wide conduct
  const [departments, setDepartments] = useState([]);
  const [deptDialog, setDeptDialog] = useState(false);
  const [deptName, setDeptName] = useState("");
  const [deptMode, setDeptMode] = useState("appreciation"); // appreciation | warning
  const [deptSeverity, setDeptSeverity] = useState("soft");
  const [deptReason, setDeptReason] = useState("");
  const [deptBusy, setDeptBusy] = useState(false);

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data || [])).catch(() => setDepartments([]));
  }, []);

  const load = useCallback(async () => {
    const propParam = selectedId ? `?property_id=${selectedId}` : "";
    const { data: users } = await api.get(`/users${propParam}`).catch(() => ({ data: [] }));
    // Pull conduct summary for each via Promise.all
    const enriched = await Promise.all(
      users.map(async (u) => {
        const [w, a] = await Promise.all([
          api.get(`/users/${u.id}/warnings`),
          api.get(`/users/${u.id}/appreciations`),
        ]);
        const active = (w.data || []).filter((x) => !x.expired_at);
        const apprs = (a.data || []).filter((x) => !x.consumed_at);
        return {
          ...u,
          hard_count: active.filter((x) => x.severity === "hard").length,
          soft_count: active.filter((x) => x.severity === "soft").length,
          appreciation_count: apprs.length,
        };
      })
    );
    setStaff(enriched);
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => staff.filter((s) => (s.name || "").toLowerCase().includes(query.toLowerCase()) || (s.email || "").toLowerCase().includes(query.toLowerCase())),
    [staff, query]
  );

  const openIssue = (user, m) => {
    setIssueFor(user);
    setMode(m);
    setSeverity("soft");
    setReason("");
  };

  const submitIssue = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason is required");
      return;
    }
    setBusy(true);
    const issuedFor = issueFor;
    const issuedMode = mode;
    const issuedSeverity = severity;
    const issuedReason = reason.trim();
    try {
      if (issuedMode === "warning") {
        const { data } = await api.post(`/users/${issuedFor.id}/warnings`, {
          severity: issuedSeverity,
          reason: issuedReason,
        });
        toast.success(
          data.auto_deactivated
            ? `${issuedFor.name} has been deactivated (3rd hard warning)`
            : `${issuedSeverity} warning issued`
        );
      } else {
        const { data } = await api.post(`/users/${issuedFor.id}/appreciations`, {
          reason: issuedReason,
        });
        toast.success(
          data.warnings_cleared
            ? `Appreciation issued · All active warnings cleared!`
            : `Appreciation issued`
        );
      }
      setIssueFor(null);
      load();
      // If admin has the email permission, offer to email it right away
      if (canEmail && issuedFor.email) {
        setEmailFor({
          user: issuedFor,
          category: issuedMode === "warning" ? "warning" : "appreciation",
          placeholders: {
            severity: issuedMode === "warning" ? issuedSeverity : "",
            reason: issuedReason,
          },
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const reactivate = async (u) => {
    if (!window.confirm(`Reactivate ${u.name}? They will regain access immediately (warnings stay on record).`)) return;
    try {
      await api.put(`/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openManage = async (u) => {
    setManageFor(u);
    setManageList([]);
    try {
      const { data } = await api.get(`/users/${u.id}/warnings`);
      setManageList((data || []).filter((w) => !w.expired_at));
    } catch (e) {
      toast.error("Could not load warnings");
    }
  };

  const removeWarning = async (w) => {
    if (!window.confirm(`Remove this ${w.severity} warning? This is permanent and can re-activate the user if they were deactivated by it.`)) return;
    setManageBusyId(w.id);
    try {
      await api.delete(`/warnings/${w.id}`);
      // If user was deactivated solely because of hard warnings count, fewer
      // hard warnings doesn't auto-reactivate — admin can hit Reactivate.
      setManageList((prev) => prev.filter((x) => x.id !== w.id));
      toast.success("Warning removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    } finally {
      setManageBusyId(null);
    }
  };

  const openDeptDialog = () => {
    setDeptName("");
    setDeptMode("appreciation");
    setDeptSeverity("soft");
    setDeptReason("");
    setDeptDialog(true);
  };

  const submitDept = async () => {
    if (!deptName) { toast.error("Pick a department"); return; }
    if (deptReason.trim().length < 3) { toast.error("Reason is required"); return; }
    setDeptBusy(true);
    try {
      if (deptMode === "appreciation") {
        const { data } = await api.post("/conduct/department/appreciate", {
          department: deptName, reason: deptReason.trim(),
        });
        toast.success(
          data.warnings_cleared_count
            ? `Appreciation sent to ${data.count} member(s) of ${deptName} · ${data.warnings_cleared_count} had warnings cleared`
            : `Appreciation sent to ${data.count} member(s) of ${deptName}`
        );
      } else {
        const { data } = await api.post("/conduct/department/warn", {
          department: deptName, severity: deptSeverity, reason: deptReason.trim(),
        });
        toast.success(
          data.deactivated_count
            ? `${deptSeverity} warning sent to ${data.count} member(s) of ${deptName} · ${data.deactivated_count} deactivated`
            : `${deptSeverity} warning sent to ${data.count} member(s) of ${deptName}`
        );
      }
      setDeptDialog(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setDeptBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="label-overline">Conduct</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Warnings &amp; Appreciations</h1>
        <p className="text-sm text-zinc-500 mt-2">Issue warnings or appreciations per team member. 3 hard warnings auto-deactivate · 3 appreciations clear all active warnings.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search team member…"
            className="pl-9"
            data-testid="warnings-admin-search"
          />
        </div>
        <Button onClick={openDeptDialog} className="bg-zinc-950 hover:bg-zinc-800" data-testid="dept-conduct-open">
          <Building2 className="w-4 h-4 mr-1.5" /> Appreciate / warn a department
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-3 px-4">Employee</th>
              <th className="py-3 px-4 text-right">Hard</th>
              <th className="py-3 px-4 text-right">Soft</th>
              <th className="py-3 px-4 text-right">Apprs.</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const tier = u.hard_count > 0 ? "hard" : u.soft_count > 0 ? "soft" : u.appreciation_count > 0 ? "appreciation" : "none";
              const tintCls = tier === "hard"
                ? "bg-red-50/40 border-l-4 border-red-500"
                : tier === "soft"
                  ? "bg-orange-50/40 border-l-4 border-orange-400"
                  : tier === "appreciation"
                    ? "bg-emerald-50/40 border-l-4 border-emerald-500"
                    : "";
              return (
              <tr key={u.id} className={`border-b border-zinc-100 ${tintCls}`} data-testid={`warnings-row-${u.id}`} data-conduct-tier={tier}>
                <td className="py-3 px-4">
                  <div className="font-medium">{u.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{u.employee_id || "—"} · {(u.role || "").replace("_", " ")}</div>
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => u.hard_count > 0 && openManage(u)}
                    disabled={u.hard_count === 0}
                    className={`font-mono font-bold underline-offset-2 ${hardCountClass(u.hard_count)}`}
                    data-testid={`hard-count-${u.id}`}
                  >
                    {u.hard_count}
                  </button>
                </td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => u.soft_count > 0 && openManage(u)}
                    disabled={u.soft_count === 0}
                    className={`font-mono font-bold underline-offset-2 ${softCountClass(u.soft_count)}`}
                    data-testid={`soft-count-${u.id}`}
                  >
                    {u.soft_count}
                  </button>
                </td>
                <td className="py-3 px-4 text-right">
                  <span className={`font-mono font-bold ${u.appreciation_count > 0 ? "text-emerald-600" : "text-zinc-400"}`}>{u.appreciation_count}</span>
                </td>
                <td className="py-3 px-4 text-center">
                  {u.active === false ? (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-red-50 text-red-700">Deactivated</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">Active</span>
                  )}
                </td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openIssue(u, "warning")} className="border-orange-300 text-orange-700 hover:bg-orange-50" data-testid={`btn-warn-${u.id}`}>
                    <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Warn
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openIssue(u, "appreciation")} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`btn-appreciate-${u.id}`}>
                    <Sparkles className="w-3.5 h-3.5 mr-1" /> Appreciate
                  </Button>
                  {(u.hard_count + u.soft_count) > 0 && (
                    <Button size="sm" variant="outline" onClick={() => openManage(u)} className="border-zinc-300" data-testid={`btn-manage-${u.id}`}>
                      <Settings2 className="w-3.5 h-3.5 mr-1" /> Manage
                    </Button>
                  )}
                  {u.active === false && (
                    <Button size="sm" variant="outline" onClick={() => reactivate(u)} className="border-zinc-300" data-testid={`btn-reactivate-${u.id}`}>
                      <Power className="w-3.5 h-3.5 mr-1" /> Reactivate
                    </Button>
                  )}
                  {canEmail && (
                    <Button size="sm" variant="outline" onClick={() => setEmailFor({ user: u, category: "custom" })} className="border-zinc-300" data-testid={`btn-email-${u.id}`}>
                      <Send className="w-3.5 h-3.5 mr-1" /> Email
                    </Button>
                  )}
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-zinc-400">No team members in scope.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!issueFor} onOpenChange={(o) => !o && setIssueFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {mode === "warning" ? `Issue warning to ${issueFor?.name}` : `Appreciate ${issueFor?.name}`}
            </DialogTitle>
            <DialogDescription>
              {mode === "warning"
                ? "Hard warnings count toward auto-deactivation at 3. Soft warnings are documented but don't deactivate."
                : "3 appreciations clear all active warnings for this employee."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {mode === "warning" && (
              <div>
                <Label>Severity</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger data-testid="issue-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="soft">
                      <span className="inline-flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-orange-600" /> Soft (orange)
                      </span>
                    </SelectItem>
                    <SelectItem value="hard">
                      <span className="inline-flex items-center gap-2">
                        <ShieldAlert className="w-3.5 h-3.5 text-red-600" /> Hard (red)
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Reason</Label>
              <Textarea
                rows={4}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={mode === "warning" ? "What did they do wrong?" : "What did they do well?"}
                data-testid="issue-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submitIssue}
              disabled={busy || reason.trim().length < 3}
              className={issueButtonClass(mode, severity)}
              data-testid="issue-submit"
            >
              <RotateCw className="w-4 h-4 mr-1.5" hidden={!busy} />
              {mode === "warning" ? `Issue ${severity} warning` : "Issue appreciation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!manageFor} onOpenChange={(o) => !o && setManageFor(null)}>
        <DialogContent className="max-w-lg" data-testid="manage-warnings-dialog">
          <DialogHeader>
            <DialogTitle>Active warnings for {manageFor?.name}</DialogTitle>
            <DialogDescription>
              {canRemove
                ? "Removing a warning is permanent. If the user was auto-deactivated by their 3rd hard warning, removing one will NOT auto-reactivate them — use the Reactivate button on the main table."
                : "View-only — ask a super-admin or someone with the 'Remove/Withdraw Warnings' permission to take action."}
            </DialogDescription>
          </DialogHeader>
          {!canRemove && (
            <div className="text-[11px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded px-3 py-2 inline-flex items-center gap-1.5" data-testid="manage-warnings-no-perm">
              <Lock className="w-3.5 h-3.5" /> You don't have the <span className="font-mono">warnings_remove</span> permission.
            </div>
          )}
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {manageList.length === 0 ? (
              <div className="text-center text-zinc-400 py-6 text-sm">No active warnings.</div>
            ) : (
              manageList.map((w) => {
                const isHard = w.severity === "hard";
                const Icon = isHard ? ShieldAlert : AlertTriangle;
                return (
                  <div
                    key={w.id}
                    className={`border-l-4 rounded-md p-3 ${isHard ? "border-red-500 bg-red-50" : "border-orange-500 bg-orange-50"}`}
                    data-testid={`manage-warning-${w.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold ${isHard ? "text-red-700" : "text-orange-700"}`}>
                          <Icon className="w-3.5 h-3.5" /> {isHard ? "Hard" : "Soft"} · {new Date(w.created_at).toLocaleDateString()}
                        </span>
                        <div className="mt-1 text-sm font-medium text-zinc-900">{w.reason}</div>
                        <div className="mt-1 text-[11px] text-zinc-500">By {w.issued_by_name}</div>
                      </div>
                      {canRemove && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50 shrink-0"
                          disabled={manageBusyId === w.id}
                          onClick={() => removeWarning(w)}
                          data-testid={`remove-warning-${w.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={deptDialog} onOpenChange={setDeptDialog}>
        <DialogContent className="max-w-md" data-testid="dept-conduct-dialog">
          <DialogHeader>
            <DialogTitle>Appreciate or warn a department</DialogTitle>
            <DialogDescription>
              This applies to every <strong>active</strong> member of the chosen department within your scope. Each member gets their own record (and the usual rules apply — 3 appreciations clear a person's warnings; 3 hard warnings auto-deactivate).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Department</Label>
              <Select value={deptName} onValueChange={setDeptName}>
                <SelectTrigger data-testid="dept-conduct-department"><SelectValue placeholder="Pick a department" /></SelectTrigger>
                <SelectContent>
                  {departments.map((d) => <SelectItem key={d.id || d.name} value={d.name}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Action</Label>
              <Select value={deptMode} onValueChange={setDeptMode}>
                <SelectTrigger data-testid="dept-conduct-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="appreciation">
                    <span className="inline-flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-emerald-600" /> Appreciate</span>
                  </SelectItem>
                  <SelectItem value="warning">
                    <span className="inline-flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-orange-600" /> Warn</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {deptMode === "warning" && (
              <div>
                <Label>Severity</Label>
                <Select value={deptSeverity} onValueChange={setDeptSeverity}>
                  <SelectTrigger data-testid="dept-conduct-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="soft">
                      <span className="inline-flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-orange-600" /> Soft (orange)</span>
                    </SelectItem>
                    <SelectItem value="hard">
                      <span className="inline-flex items-center gap-2"><ShieldAlert className="w-3.5 h-3.5 text-red-600" /> Hard (red)</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Reason</Label>
              <Textarea
                rows={4}
                value={deptReason}
                onChange={(e) => setDeptReason(e.target.value)}
                placeholder={deptMode === "warning" ? "What went wrong across the department?" : "What did the department do well?"}
                data-testid="dept-conduct-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={submitDept}
              disabled={deptBusy || !deptName || deptReason.trim().length < 3}
              className={deptMode === "appreciation" ? "bg-emerald-600 hover:bg-emerald-700" : deptSeverity === "hard" ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}
              data-testid="dept-conduct-submit"
            >
              <RotateCw className="w-4 h-4 mr-1.5" hidden={!deptBusy} />
              {deptMode === "appreciation" ? "Appreciate department" : `Issue ${deptSeverity} warning to department`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SendEmailDialog
        open={!!emailFor}
        onOpenChange={(v) => !v && setEmailFor(null)}
        title={`Email ${emailFor?.user?.name || ""}`}
        defaultCategory={emailFor?.category || "custom"}
        toUser={emailFor?.user ? { id: emailFor.user.id, name: emailFor.user.name, email: emailFor.user.email } : null}
        extraPlaceholders={emailFor?.placeholders || {}}
      />
    </div>
  );
}
